import { PlayerSwap } from "@/api/privateRequests/sbcs/player-swap";
import type { PutUserSbcSolutionPlayer } from "@/api/privateRequests/sbcs/putUserSbcSolution";
import { useClubImportNudge } from "@/components/features/ClubImport/useClubImportNudge";
import {
  trackPlayerSwapAccept,
  trackSolveSbc,
  trackSolveSbcAgain,
} from "@/events/sbcMixpanel";
import { formatSbcSetName } from "@/pages/Sbcs/SbcSetPage/SbcSetPage";
import { addAlert, setMessage } from "@/redux/message/messageSlice";
import {
  removeIncludedPlayer,
  sbcFiltersSelector,
} from "@/redux/sbcs/sbcFiltersSlice";
import {
  getSbcSelector,
  getSbcSetSelector,
  getSbcsSelector,
  markSbcSolved,
  selectedSolutionSelector,
  solveSbc,
  updateSolution,
} from "@/redux/sbcs/sbcsSlice";
import { AppDispatch } from "@/redux/store";
import {
  fetchUser,
  getUserSelector,
  solvesWithClubSelector,
} from "@/redux/user/userSlice";
import { Player } from "@/types/SBCPlayer";
import { useTranslation } from "@repo/shared/i18n";
import { useCreditGate } from "@repo/shared/redux";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import {
  deleteSpecificPlayers,
  putUserSbcSolution,
} from "../../api/privateApi";
import SolutionView from "../../components/organisms/SolutionView/SolutionView";
import "../../redux/user/userSlice";

const mapSolutionPlayersForPutBody = (
  players: Player[] | undefined,
): PutUserSbcSolutionPlayer[] => {
  if (!players?.length) {
    return [];
  }
  return players
    .filter((p) => p.resource_id != null)
    .map((p) => ({
      resourceId: p.resource_id as number,
      ...(p.id != null ? { eaPlayerId: p.id } : {}),
    }));
};

export const SbcSolutionPage = () => {
  const { t } = useTranslation("sbcs");
  const dispatch = useDispatch<AppDispatch>();
  const { sbcId, setId, setName } = useParams();
  const sbc = useSelector(getSbcSelector(+sbcId, +setId));
  const sbcSet = useSelector(getSbcSetSelector(+setId));
  const solution = useSelector(selectedSolutionSelector);
  const { solutionSbcId, solutionStatus } = useSelector(getSbcsSelector);
  const { user } = useSelector(getUserSelector);
  const filters = useSelector(sbcFiltersSelector);
  const solvesWithClub = useSelector(solvesWithClubSelector);
  const creditGate = useCreditGate("solve-sbc-club");
  const nudgeClubImport = useClubImportNudge("sbc-solver");
  const scope = solvesWithClub ? "club" : "market";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* A deep link lands here with nothing in the store, so the page solves
     itself, once, behind the same gate the set page's card uses. A visitor
     who came through the card already has this challenge's squads. */
  useEffect(() => {
    if (solutionSbcId === +sbcId || solutionStatus === "pending") return;
    if (!creditGate({ qualifying: solvesWithClub, sbcId: +sbcId })) return;
    dispatch(solveSbc(+sbcId));
    nudgeClubImport({ subsection: "Solution View" });
    trackSolveSbc({
      subsection: "Solution View",
      categoryId: sbcSet?.categoryId,
      filters,
      scope,
    });
    // Once per challenge: the store, not the render, decides whether it ran.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sbcId]);

  const onSwap = (player: PlayerSwap, index: number) => {
    const { players } = solution;
    const newPlayers = [...players];
    const formation = sbc.formation;

    for (let i = 0; i < newPlayers.length; i++) {
      const p = { ...newPlayers[i] };
      p.chemistry = player.new_solution_info.player_chems[i];
    }
    const untradeablesCount = player.new_solution_info.n_untradeables;
    const ownedPlayersCount = player.new_solution_info.n_owned_players;
    const rating = player.new_solution_info.rating;
    const chem = player.new_solution_info.total_chem;
    const transferMarketCost = player.new_solution_info.transfer_market_cost;
    const duplicatesCount = player.new_solution_info.n_duplicates;
    const cost = player.new_solution_info.price;
    newPlayers[index] = player;

    dispatch(
      updateSolution({
        ...solution,
        cost,
        chem,
        rating,
        players: newPlayers,
        formation,
        untradeablesCount,
        ownedPlayersCount,
        transferMarketCost,
        duplicatesCount,
      }),
    );
    trackPlayerSwapAccept();
  };

  const handleUncompleteSBC = () => {
    dispatch(
      markSbcSolved({
        sbcId: sbc.id,
        setId: sbc.setId,
        solved: false,
      }),
    );
  };

  const handleCompleteAndRemovePlayers = async (solved: boolean) => {
    const ownedPlayers = solution?.players?.filter(
      (player) => player.user_owned,
    );
    if (!user.uuid) {
      dispatch(
        setMessage({
          title: t("alerts.loginToMarkSolved"),
        }),
      );
      return;
    }
    if (solved) {
      await putUserSbcSolution(sbc.setId, sbc.id, {
        players: mapSolutionPlayersForPutBody(solution?.players),
      });
      // Every owned card in the squad leaves the club, whichever pool built it.
      if (ownedPlayers?.length > 0) {
        await deleteSpecificPlayers(ownedPlayers?.map((player) => player.id));
        dispatch(fetchUser({ didLogin: false }));
      }
      solution.players.forEach((p) =>
        dispatch(removeIncludedPlayer(p.resource_id)),
      );

      const count = ownedPlayers?.length ?? 0;
      const desc =
        count === 0 ? "" : t("alerts.playersRemovedFromImport", { count });

      dispatch(
        addAlert({
          title: t("alerts.completed", { name: sbc?.name }),
          description: desc,
          type: "success",
        }),
      );
    }
    // Unlimited repeatable sets are never completed, same as on the sets list.
    if (sbcSet?.repeatabilityMode === "UNLIMITED") return;
    dispatch(
      markSbcSolved({
        sbcId: sbc.id,
        setId: sbc.setId,
        solved: solved,
      }),
    );
  };

  return (
    <div>
      <SolutionView
        solution={solution}
        sbc={sbc}
        sbcSetName={sbcSet?.name ?? formatSbcSetName(setName)}
        setReward={sbcSet?.reward}
        setDescription={sbcSet?.description}
        onSwap={onSwap}
        onHandleSolveAgain={() => {
          if (!creditGate({ qualifying: solvesWithClub, sbcId: +sbcId }))
            return;
          dispatch(solveSbc(+sbcId));
          nudgeClubImport({ subsection: "Solution View" });
          trackSolveSbcAgain({
            subsection: "Solution View",
            categoryId: sbcSet?.categoryId,
            filters,
            scope,
          });
        }}
        onHandleCompleteAndRemovePlayers={() =>
          handleCompleteAndRemovePlayers(true)
        }
        handleUncompleteSBC={handleUncompleteSBC}
      />
    </div>
  );
};

export default SbcSolutionPage;
